import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getModels, refreshModels } from '../../api/admin'

export default function ModelsPage() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [providerFilter, setProviderFilter] = useState('')

  const { data: models, isLoading } = useQuery({
    queryKey: ['admin-models'],
    queryFn: getModels,
  })

  const refreshMutation = useMutation({
    mutationFn: refreshModels,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-models'] })
      alert(`Models refreshed! ${data.count} models available.`)
    },
    onError: (error: Error) => {
      alert(`Error refreshing models: ${error.message}`)
    },
  })

  // Get unique providers
  const providers = [...new Set(models?.map(m => m.id.split('/')[0]) || [])]

  // Filter models
  const filteredModels = models?.filter(m => {
    const matchesSearch = !search ||
      m.id.toLowerCase().includes(search.toLowerCase()) ||
      m.name.toLowerCase().includes(search.toLowerCase())
    const matchesProvider = !providerFilter || m.id.startsWith(providerFilter + '/')
    return matchesSearch && matchesProvider
  })

  // Group by provider for display
  const modelsByProvider = filteredModels?.reduce((acc, m) => {
    const provider = m.id.split('/')[0]
    if (!acc[provider]) acc[provider] = []
    acc[provider].push(m)
    return acc
  }, {} as Record<string, typeof models>)

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">LLM Models</h1>
          <p className="text-gray-500 text-sm mt-1">
            {models?.length || 0} models available from OpenRouter
          </p>
        </div>
        <button
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
          className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition disabled:opacity-50"
        >
          {refreshMutation.isPending ? 'Refreshing...' : 'Refresh Models'}
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search models..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
          />
        </div>
        <select
          value={providerFilter}
          onChange={(e) => setProviderFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
        >
          <option value="">All Providers</option>
          {providers.map(provider => (
            <option key={provider} value={provider}>
              {provider}
            </option>
          ))}
        </select>
      </div>

      {/* Models list */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl shadow p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-64"></div>
            </div>
          ))}
        </div>
      ) : modelsByProvider && Object.keys(modelsByProvider).length > 0 ? (
        <div className="space-y-6">
          {Object.entries(modelsByProvider).map(([provider, providerModels]) => (
            <div key={provider} className="bg-white rounded-xl shadow overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b">
                <h3 className="font-semibold text-gray-800">{provider}</h3>
                <p className="text-sm text-gray-500">{providerModels?.length} models</p>
              </div>
              <div className="divide-y">
                {providerModels?.map(model => (
                  <div key={model.id} className="px-6 py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-gray-800">{model.name}</h4>
                        <p className="text-sm text-gray-500 font-mono">{model.id}</p>
                        {model.description && (
                          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                            {model.description.substring(0, 150)}
                            {model.description.length > 150 && '...'}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {model.supports_vision && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded">
                            Vision
                          </span>
                        )}
                        {model.supports_functions && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded">
                            Functions
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-6 mt-3 text-sm text-gray-500">
                      <span>Context: {model.context_length.toLocaleString()}</span>
                      <span>Prompt: ${(model.pricing_prompt * 1000000).toFixed(2)}/M</span>
                      <span>Completion: ${(model.pricing_completion * 1000000).toFixed(2)}/M</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow p-8 text-center">
          <p className="text-gray-500 mb-4">No models found</p>
          <button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition"
          >
            Refresh Models
          </button>
        </div>
      )}
    </div>
  )
}
